import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import PaperPlaneIcon from '../../svgs/PaperPlaneIcon';

import type { OtherVideo } from '../../types.ts/story';

type Props = {
  video: OtherVideo;
  iconOnly?: boolean;
};

const ClientReadyFlag = observer(({ video, iconOnly = false }: Props) => {
  const userIdentityStore = useStore().userIdentityStore;
  const videoCreator = useVideoCreatorStore();
  const [isApproved, setIsApproved] = useState(video.isClientReady);

  useEffect(() => {
    setIsApproved(video.isClientReady);
  }, [video.isClientReady]);

  const toggleApprove = () => {
    setIsApproved(!video.isClientReady);
    videoCreator.updateIsClientReady(!video.isClientReady, video.id);
  };

  if (userIdentityStore.currentUserType === 'external') {
    return null;
  }

  return (
    <Container
      onClick={toggleApprove}
      isApproved={isApproved}
      iconOnly={iconOnly}
    >
      <PaperPlaneIcon
        width="14"
        height="14"
        strokeColor={
          isApproved
            ? iconOnly
              ? 'var(--text-secondary)'
              : 'var(--background)'
            : 'var(--border)'
        }
      />
      {!iconOnly && <Status isApproved={isApproved}>APPROVED</Status>}
    </Container>
  );
});

const Container = styled.div<{
  isApproved?: boolean;
  iconOnly?: boolean;
}>`
  background: ${props => (props.iconOnly ? 'transparent' : 'var(--accent)')};
  border: ${props => (props.iconOnly ? 'none' : '1px solid var(--accent)')};
  padding: ${props => (props.iconOnly ? '0' : '4px 8px')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: var(--transition-normal) ease;
  ${props =>
    !props.isApproved &&
    css`
      background: inherit;
      border-color: var(--border);
    `}
  ${props =>
    props.iconOnly &&
    css`
      svg path {
        transition: var(--transition-normal) ease;
      }
    `}
`;

const Status = styled.div<{
  isApproved?: boolean;
}>`
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  font-weight: var(--font-weight-bold);
  line-height: 10px;
  padding-left: 8px;
  color: var(--background);
  ${props =>
    !props.isApproved &&
    css`
      color: var(--border);
      text-decoration: line-through;
    `}
`;

ClientReadyFlag.displayName = 'ClientReadyFlag';

export default ClientReadyFlag;
